import React, { useState, useEffect, useRef } from 'react';
import {
  ChatBotWrapper,
  ChatPopup,
  ChatMessages,
  Message,
  UserInputWrapper,
  UserInput,
  SendButton,
  VoiceButton,
  ListeningPopup,
  CloseButton,
  FAQContainer,
  FAQList,
  FAQButton,
} from './chatBotNew.style';
import { getChatBotRespondNoAuth } from '../../../services/chatBot';
import { Mic, MicOff, X } from 'lucide-react';
import { useLanguage } from '../../../hooks/useLanguageSwitch';
import { FAQ_QUESTIONS } from './constant';
import { formatBotMessage } from '../../../helper/aiRespondFormatter';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

const ChatBotModal = ({ isOpen, onClose }) => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const lang = useLanguage();

  const [selectedLanguage, setSelectedLanguage] = useState('en-US');

  useEffect(() => {
    if (lang?.selectedLang) {
      setSelectedLanguage(lang.selectedLang);
    }
  }, [lang?.selectedLang]);

  const handleSend = async (messageText = input) => {
    if (!messageText.trim()) return;

    const userMsg = { type: 'user', text: messageText };
    setMessages((prev) => [...prev, userMsg]);

    // Clear input only if it's from the input field
    if (messageText === input) {
      setInput('');
    }

    setLoading(true);

    try {
      const response = await getChatBotRespondNoAuth({ question: messageText });

      // More robust response handling
      let botResponse = 'No response from bot.';
      if (response) {
        if (typeof response === 'string') {
          botResponse = response;
        } else if (response.output) {
          botResponse = response.output;
        } else if (response.data && response.data.output) {
          botResponse = response.data.output;
        } else if (response.message) {
          botResponse = response.message;
        } else if (response.answer) {
          botResponse = response.answer;
        }
      }

      const botMsg = { type: 'bot', text: botResponse };
      setMessages((prev) => [...prev, botMsg]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { type: 'bot', text: 'Error fetching response. Please try again.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Handle FAQ question click
  const handleFAQClick = (question) => {
    handleSend(question);
  };

  // Initialize Speech Recognition
  useEffect(() => {
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.lang = 'en-US';
      recognition.continuous = false;
      recognition.interimResults = false;

      recognition.onresult = (event) => {
        const speechResult = event.results[0][0].transcript;
        setInput((prev) => `${prev} ${speechResult}`.trim());
      };

      recognition.onerror = (event) => {
        // eslint-disable-next-line no-console
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognition.onend = () => setIsListening(false);
      recognitionRef.current = recognition;
    }
  }, []);

  const handleStartListening = async () => {
    if (!recognitionRef.current) return;

    try {
      const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
      if (permissionStatus.state === 'denied') {
        alert(
          'Microphone access is blocked. Please allow microphone permissions in your browser settings.'
        );
        return;
      }

      recognitionRef.current.lang = selectedLanguage;
      recognitionRef.current.start();
      setIsListening(true);
    } catch (error) {
      alert('Unable to access the microphone. Please check your browser settings.');
    }
  };

  const handleStopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  // Close modal when clicking outside
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  useEffect(() => {
    if (!isOpen && isListening) {
      handleStopListening();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <ChatBotWrapper
      aria-live="polite"
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '20px',
        boxSizing: 'border-box',
      }}
    >
      <ChatPopup
        role="dialog"
        aria-modal="true"
        isOpen={isOpen}
        style={{
          width: '100%',
          maxWidth: '900px',
          height: '70%',
          position: 'relative',
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          transform: 'translate(0, 0)',
          borderRadius: '16px',
        }}
      >
        {/* Close Button */}
        <CloseButton onClick={onClose} aria-label="Close Chatbot Modal">
          <X size={20} />
        </CloseButton>

        {/* FAQ Section - Show only when there are no messages and not loading */}
        {messages.length === 0 && !loading && (
          <FAQContainer style={{ marginTop: '40px' }}>
            <FAQList>
              {FAQ_QUESTIONS.map((question, index) => (
                <FAQButton
                  key={index}
                  onClick={() => handleFAQClick(question)}
                  aria-label={`Ask: ${question}`}
                >
                  {question}
                </FAQButton>
              ))}
            </FAQList>
            <div
              style={{
                fontSize: '12px',
                color: '#0f0f0f',
                textAlign: 'center',
                marginTop: '16px',
                fontStyle: 'italic',
              }}
            >
              Or feel free to type your own question below
            </div>
          </FAQContainer>
        )}

        <ChatMessages
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: messages.length === 0 && !loading ? '20px' : '50px 20px 20px',
          }}
        >
          {messages.map((msg, index) => (
            <Message key={index} user={msg.type === 'user'}>
              {msg.type === 'bot' ? formatBotMessage(msg.text) : msg.text}
            </Message>
          ))}
          {loading && <span className="loaderSecondary"></span>}
        </ChatMessages>

        <UserInputWrapper style={{ padding: '20px' }}>
          {messages.length > 0 && (
            <div
              style={{
                fontSize: '11px',
                color: '#242424',
                textAlign: 'center',
                marginBottom: '12px',
                padding: '0 12px',
                lineHeight: '1.4',
              }}
            >
              Have another question? Feel free to type it below or use the voice input feature
            </div>
          )}

          <div
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <UserInput
              aria-label="Type your message"
              placeholder="Type your message or use Voice"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              readOnly={isListening}
              style={{
                flex: 1,
                minWidth: 0,
                width: '100%',
              }}
            />
            {/* 🎤 Voice Button with toggle */}
            <VoiceButton
              onClick={isListening ? handleStopListening : handleStartListening}
              className={isListening ? 'listening' : ''}
              aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
              style={{ flexShrink: 0 }}
            >
              {isListening ? <MicOff /> : <Mic />}
            </VoiceButton>
            {/* ➤ Send Button */}
            <SendButton
              disabled={!input.trim()}
              onClick={() => handleSend(input)}
              aria-label="Send message"
              style={{ flexShrink: 0 }}
            >
              ➤
            </SendButton>
          </div>

          {/* Listening Pop-up */}
          {isListening && (
            <ListeningPopup
              style={{
                position: 'absolute',
                bottom: '80px',
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            >
              🎤 Listening...
            </ListeningPopup>
          )}
        </UserInputWrapper>
      </ChatPopup>
    </ChatBotWrapper>
  );
};

export default ChatBotModal;
